import { ApiErrorKind, isApiError } from '../api/apiError';
import { OP } from '../api/operations';

const DEFAULTS_BY_KIND = Object.freeze({
  [ApiErrorKind.Network]: {
    title: 'Network error',
    message: "Can’t reach the server right now. Check your connection and try again.",
  },
  [ApiErrorKind.Unauthorized]: {
    title: 'Session expired',
    message: 'Please sign in again and retry your action.',
  },
  [ApiErrorKind.NotFound]: {
    title: 'Not found',
    message: 'The requested item could not be found. Please refresh and try again.',
  },
  [ApiErrorKind.Validation]: {
    title: 'Check your inputs',
    message: 'Some inputs look invalid. Please review and try again.',
  },
  [ApiErrorKind.Conflict]: {
    title: 'Already exists',
    message: 'That value is already in use. Please choose a different one and try again.',
  },
  [ApiErrorKind.Server]: {
    title: 'Server error',
    message: 'Something went wrong on the server. Please try again in a moment.',
  },
  [ApiErrorKind.Unknown]: {
    title: 'Something went wrong',
    message: 'Please try again. If the issue persists, refresh the page.',
  },
});

// Operation-specific (user-safe) messages.
const BY_OPERATION = Object.freeze({
  [OP.pipeline.create]: {
    title: 'Couldn’t create pipeline',
    message: 'Try a different pipeline name and try again.',
  },
  [OP.pipeline.delete]: { title: 'Couldn’t delete pipeline', message: 'Please try again.' },
  [OP.pipeline.copy]: { title: 'Couldn’t copy pipeline', message: 'Please try again.' },
  [OP.pipeline.runByName]: {
    title: 'Couldn’t run pipeline',
    message: 'Please review your node configuration and try again.',
  },
  [OP.node.updatePosition]: {
    title: 'Couldn’t save node position',
    message: 'Please try again.',
  },
  [OP.node.deleteFromPipeline]: {
    title: 'Couldn’t delete node',
    message: 'Please try again. If it keeps failing, refresh the page.',
  },
  [OP.node.reconfigure]: {
    title: 'Couldn’t reconfigure node',
    message: 'Connect a valid input node and try again.',
  },
  [OP.node.appendScaler]: {
    title: 'Couldn’t add Scaler node',
    message: 'Check the node name and input connection, then try again.',
  },
  [OP.node.editScaler]: {
    title: 'Couldn’t update Scaler node',
    message: 'Please check your inputs and try again.',
  },
  [OP.node.appendNormalizer]: {
    title: 'Couldn’t add Normalizer node',
    message: 'Check the node name and input connection, then try again.',
  },
  [OP.node.editNormalizer]: {
    title: 'Couldn’t update Normalizer node',
    message: 'Please check your inputs and try again.',
  },
  [OP.node.appendCapper]: {
    title: 'Couldn’t add Capper node',
    message: 'Check the node name and input connection, then try again.',
  },
  [OP.node.editCapper]: {
    title: 'Couldn’t update Capper node',
    message: 'Please check your inputs and try again.',
  },
  [OP.genUpload.uploadDataFile]: {
    title: 'Couldn’t upload file',
    message: 'Please verify the file format and try again.',
  },
});

function isSafeToShowServerMessage(serverMessage) {
  if (!serverMessage || typeof serverMessage !== 'string') return false;
  const s = serverMessage.trim();
  if (!s) return false;
  if (s.length > 180) return false;
  if (/traceback|stack|exception/i.test(s)) return false;
  return true;
}

/**
 * Convert any thrown error into a user-facing message object.
 * @param {unknown} err
 * @param {string} [operation]
 */
export function getUserFacingError(err, operation) {
  if (isApiError(err)) {
    const op = operation || err.operation;
    const base =
      (op && BY_OPERATION[op]) ||
      DEFAULTS_BY_KIND[err.kind] ||
      DEFAULTS_BY_KIND[ApiErrorKind.Unknown];

    // Only show serverMessage for validation-ish failures, and only if safe.
    const shouldAppendServerMessage =
      err.kind === ApiErrorKind.Validation && isSafeToShowServerMessage(err.serverMessage);

    return {
      title: base.title,
      message: shouldAppendServerMessage ? `${base.message} (${err.serverMessage})` : base.message,
      kind: err.kind,
      status: err.status,
      operation: op,
      retryable: err.retryable,
    };
  }

  // Non-ApiError fallback
  const message =
    (err && typeof err === 'object' && typeof err.message === 'string' && err.message) ||
    DEFAULTS_BY_KIND[ApiErrorKind.Unknown].message;

  return {
    title: DEFAULTS_BY_KIND[ApiErrorKind.Unknown].title,
    message,
    kind: ApiErrorKind.Unknown,
    status: null,
    operation: operation || '',
    retryable: false,
  };
}

